/**
 * Shifts page — your work period and the patients in it.
 *
 * A shift is the nurse's WORK PERIOD; patients attach to it as assignments, so a
 * nurse cares for MANY patients within one shift (no per-patient "shift", no
 * one-patient cap). On duty: see your patient load, add a patient, hand one
 * over to an incoming nurse, end one, or end the shift.
 *
 * A patient has NO name — patient options render a config-derived label only.
 * Shift/assignment mutations are server-authoritative (real clinical + ACL
 * records); the four async states are handled for every fetch.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCw, UserRoundPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/clinical';
import HandoverDialog from '@/components/handover/HandoverDialog';
import { ShiftJourney } from '@/components/shifts/ShiftJourney';
import { cn } from '@/lib/utils';
import { usePatients, usePatientsConfig } from '@/hooks/usePatients';
import {
  useActiveShiftAssignments,
  useAssignPatient,
  useEndAssignment,
  useEndShift,
} from '@/hooks/useShifts';
import { formatPatientLabel } from '@/lib/patientLabel';
import { MAIN_APP_ROUTES } from '@/lib/routes';
import type { AssignmentResponse } from '@/api/endpoints/shifts';

export default function Shifts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = usePatientsConfig();
  const labelFormat = config?.label_format;

  const {
    openShift,
    assignments,
    patientIds,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useActiveShiftAssignments();

  // 'all', not the in_progress default: a patient already on this shift's
  // load (assignments/patientIds, below) may have been discharged mid-shift —
  // labelFor() still needs to resolve their codename, not fall back to
  // "Unknown patient". addablePatients (below) narrows back to in_progress
  // only, since a discharged patient is never a valid ADD candidate.
  const {
    data: patients,
    isLoading: patientsLoading,
    isError: patientsError,
    isFetching: patientsFetching,
    refetch: refetchPatients,
  } = usePatients('all');

  const assignPatient = useAssignPatient();
  const endAssignment = useEndAssignment();
  const endShift = useEndShift();

  const [patientToken, setPatientToken] = useState('');
  // assignment currently being handed over (opens the shared HandoverDialog).
  const [handoverFor, setHandoverFor] = useState<AssignmentResponse | null>(null);

  const patientLabelByToken = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of patients ?? []) {
      map.set(p.token, formatPatientLabel(p.attributes, labelFormat, p.token));
    }
    return map;
  }, [patients, labelFormat]);

  // Patients not already in the shift, and still actually admitted — a
  // discharged patient (included in `patients` above for label lookups) is
  // never a sensible candidate to add to an active shift's load.
  const addablePatients = useMemo(() => {
    const inShift = new Set(patientIds);
    return (patients ?? []).filter((p) => p.status === 'in_progress' && !inShift.has(p.token));
  }, [patients, patientIds]);

  const labelFor = (token: string) =>
    patientLabelByToken.get(token) ?? t('patients.assign.unknownPatient');

  const handleEndShift = () => {
    if (!openShift || endShift.isPending) return;
    endShift.mutate(openShift.id);
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!openShift || !patientToken || assignPatient.isPending) return;
    assignPatient.mutate(
      { shiftId: openShift.id, body: { patient_id: patientToken } },
      { onSuccess: () => setPatientToken('') },
    );
  };

  const handleEndAssignment = (a: AssignmentResponse) => {
    if (!openShift || endAssignment.isPending) return;
    endAssignment.mutate({ shiftId: openShift.id, assignmentId: a.id });
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <header className="sticky top-0 z-10 bg-neutral-50/80 backdrop-blur-xl border-b border-neutral-200/50">
        <div className="px-4 h-14 flex items-center gap-3">
          <Link
            to={MAIN_APP_ROUTES.home}
            aria-label={t('common.back')}
            className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center text-neutral-600 active:scale-95 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-display font-semibold text-neutral-900">
            {t('shifts.title')}
          </h1>
        </div>
      </header>

      <main className="flex-1 min-h-0 px-4 pb-24 pt-4">
        <div className="mx-auto w-full max-w-xl space-y-6">
          {/* Where am I on the shift journey — navigation, never a gate. */}
          <ShiftJourney />

          {/* Loading (no cached shift) */}
          {isLoading && !openShift ? (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : isError && !openShift ? (
            <RetryRow
              label={t('shifts.loadError', { defaultValue: 'Could not load your shift.' })}
              onRetry={() => refetch()}
              isFetching={isFetching}
            />
          ) : !openShift ? (
            /* OFF DUTY */
            <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center">
              <p className="text-[17px] font-semibold text-neutral-900">
                {t('shifts.offDuty.title', { defaultValue: "You're not on shift" })}
              </p>
            </div>
          ) : (
            /* ON DUTY — patient load */
            <>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4">
                <div>
                  <p className="text-[15px] font-semibold text-neutral-900">
                    {t('shifts.onDuty.title', { defaultValue: 'On shift' })}
                  </p>
                  <p className="text-[13px] text-neutral-500">
                    {t('shifts.onDuty.patientCount', {
                      defaultValue_one: '{{count}} patient',
                      defaultValue_other: '{{count}} patients',
                      count: patientIds.length,
                    })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEndShift}
                  disabled={endShift.isPending}
                  className="min-h-[44px]"
                >
                  {endShift.isPending
                    ? t('shifts.end.submitting', { defaultValue: 'Ending…' })
                    : t('shifts.end.submit', { defaultValue: 'End shift' })}
                </Button>
              </div>

              {/* My patients */}
              <section className="space-y-2">
                <h2 className="text-[13px] font-semibold text-neutral-500 uppercase tracking-wide">
                  {t('shifts.myPatients', { defaultValue: 'My patients' })}
                </h2>
                {assignments.length === 0 ? (
                  <EmptyState
                    title={t('shifts.noPatients.title', {
                      defaultValue: 'No patients yet',
                    })}
                    description={t('shifts.noPatients.description', {
                      defaultValue: 'Add a patient to start caring for them.',
                    })}
                  />
                ) : (
                  <ul className="space-y-2">
                    {assignments.map((a) => (
                      <li
                        key={a.id}
                        className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[15px] font-medium text-neutral-900 truncate">
                            {labelFor(a.patient_id)}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHandoverFor(a)}
                              className="min-h-[44px]"
                            >
                              {t('shifts.handover.action', { defaultValue: 'Hand over' })}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEndAssignment(a)}
                              disabled={endAssignment.isPending}
                              aria-label={t('shifts.endAssignment.action', {
                                defaultValue: 'End',
                              })}
                              className="min-h-[44px] min-w-[44px] text-neutral-600"
                            >
                              <X className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Add a patient */}
              <form
                onSubmit={handleAdd}
                className="space-y-3 rounded-lg border border-neutral-200 bg-white p-5"
              >
                <h2 className="text-[13px] font-semibold text-neutral-500 uppercase tracking-wide">
                  {t('shifts.addPatient.title', { defaultValue: 'Add a patient' })}
                </h2>
                <AddPatientSelect
                  value={patientToken}
                  onChange={setPatientToken}
                  options={addablePatients.map((p) => ({
                    token: p.token,
                    label: labelFor(p.token),
                  }))}
                  isLoading={patientsLoading}
                  isError={patientsError}
                  isFetching={patientsFetching}
                  onRetry={() => void refetchPatients()}
                />
                <Button
                  type="submit"
                  size="pill"
                  disabled={!patientToken || assignPatient.isPending}
                  className="w-full min-h-[44px] text-[17px]"
                >
                  <UserRoundPlus className="w-4 h-4 mr-1" />
                  {assignPatient.isPending
                    ? t('shifts.addPatient.submitting', { defaultValue: 'Adding…' })
                    : t('shifts.addPatient.submit', { defaultValue: 'Add patient' })}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>

      {/* Shared handover flow — the SAME dialog the Home patient workspace
          mounts: nurse picker + conversation curation + note. Block ONLY
          self-handover; on-shift colleagues are valid
          targets — do NOT re-exclude them (that was the one-patient cap). */}
      {handoverFor && openShift && (
        <HandoverDialog
          open={!!handoverFor}
          onOpenChange={(open) => !open && setHandoverFor(null)}
          patientId={handoverFor.patient_id}
          patientLabel={labelFor(handoverFor.patient_id)}
          excludeNurseIds={[openShift.nurse_id]}
          onStarted={(session) => navigate(`/sessions/${session.id}`)}
        />
      )}
    </div>
  );
}

interface AddPatientSelectProps {
  value: string;
  onChange: (token: string) => void;
  options: { token: string; label: string }[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}

function AddPatientSelect({
  value,
  onChange,
  options,
  isLoading,
  isError,
  isFetching,
  onRetry,
}: AddPatientSelectProps) {
  const { t } = useTranslation();

  if (isLoading && options.length === 0) {
    return <Skeleton className="h-11 w-full" />;
  }
  if (isError && options.length === 0) {
    return (
      <RetryRow
        label={t('patients.list.loadError.title')}
        onRetry={onRetry}
        isFetching={isFetching}
      />
    );
  }
  if (options.length === 0) {
    return (
      <EmptyState
        title={t('shifts.addPatient.none.title', {
          defaultValue: 'No more patients to add',
        })}
        description={t('shifts.addPatient.none.description', {
          defaultValue: 'Every registered patient is already in your shift.',
        })}
      />
    );
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor="add-shift-patient" className="block text-[15px] font-medium text-neutral-900">
        {t('shifts.patient.label')}
      </label>
      <select
        id="add-shift-patient"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 min-h-[44px] w-full rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
      >
        <option value="">{t('shifts.patient.placeholder')}</option>
        {options.map((o) => (
          <option key={o.token} value={o.token}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function RetryRow({
  label,
  onRetry,
  isFetching,
}: {
  label: string;
  onRetry: () => void;
  isFetching: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg border border-neutral-200 p-4 flex items-center justify-between gap-3"
      role="alert"
    >
      <span className="text-[15px] text-neutral-900">{label}</span>
      <button
        type="button"
        onClick={onRetry}
        disabled={isFetching}
        className={cn(
          'inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full',
          'bg-primary text-primary-foreground text-[15px] font-semibold',
          'active:scale-95 transition disabled:opacity-50',
        )}
      >
        <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
        {t('patients.list.loadError.retry')}
      </button>
    </div>
  );
}
